import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { capitalizeWords } from 'src/utils/validations/formValidations';
import { Order } from 'src/utils/interfaces/interfaces';
import { formatText } from 'src/utils/stringUtils';

interface OrdersTableProps {
  orders: any[];
  type: string;
  onRowClick?: (order: Order) => void;
  activeOrder: string | null;
}

interface Column {
  id: string;
  label: string;
  minWidth?: number;
}

const OrdersTable = ({
  orders,
  type,
  onRowClick,
  activeOrder,
}: OrdersTableProps) => {
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const activeRowRef = useRef<HTMLTableRowElement | null>(null);
  const { t } = useTranslation();
  const [page] = useState(0);
  const rowsPerPage = 52;

  const columns: Column[] = [
    { id: 'orderNumber', label: t('orders_page.order_number'), minWidth: 170 },
    { id: 'orderType', label: t('orders_page.order_type'), minWidth: 170 },
    { id: 'orderStatus', label: t('orders_page.status'), minWidth: 170 },
    { id: 'application', label: t('orders_page.application'), minWidth: 150 },
    { id: 'startingTime', label: t('orders_page.starting_time'), minWidth: 170 },
    { id: 'validFrom', label: t('orders_page.valid_from'), minWidth: 170 },
    { id: 'validTo', label: t('orders_page.valid_to'), minWidth: 170 },
    { id: 'origin', label: t('orders_page.origin'), minWidth: 170 },
    { id: 'destination', label: t('orders_page.destination'), minWidth: 170 },
    { id: 'shippingAgency', label: t('orders_page.shipping_company'), minWidth: 170 },
  ];

  const rows = orders.map((data) => {
    const orderNumber =
      data?.orderNumber &&
        data.orderNumber !== 'N/A' &&
        !/^0+$/.test(data.orderNumber)
        ? data.orderNumber
        : `${t('order_details.data_not_provided')}`;

    const orderType =
      data?.orderType &&
        data.orderType !== 'N/A' &&
        !/^0+$/.test(data.orderType)
        ? data.orderType
        : `${t('order_details.data_not_provided')}`;

    const orderStatus =
      data?.status && data.status !== 'N/A' && !/^0+$/.test(data.status)
        ? formatText(data.status)
        : `${t('order_details.data_not_provided')}`;

    const application =
      data?.application &&
        data.application !== 'N/A' &&
        !/^0+$/.test(data.application)
        ? data.application
        : `${t('order_details.data_not_provided')}`;

    const validFrom = data?.validFrom
      ? moment(data?.validFrom).format('Do MMM YYYY, h:mm A').toUpperCase()
      : 'N/A';

    const validTo = data?.validTo
      ? moment(data?.validTo).format('Do MMM YYYY, h:mm A').toUpperCase()
      : 'N/A';

    const startingTime =
      data?.startedAt || data?.createdAt
        ? moment(data?.startedAt || data?.createdAt).format(
          'Do MMM YYYY, h:mm A'
        )
        : 'N/A';

    const arrivalTime =
      type === 'completed' && data?.completedAt
        ? moment(data?.completedAt).format('Do MMM YYYY, h:mm a')
        : undefined;

    const hsCode =
      data?.hsCode && data.hsCode !== 'N/A' && !/^0+$/.test(data.hsCode)
        ? data.hsCode
        : `${t('order_details.data_not_provided')}`;

    const origin = data?.originPort || data?.address?.origin || 'N/A';
    const destination =
      data?.destinationPort || data?.address?.destination || 'N/A';

    const shippingAgency =
      data?.shippingCompany?.name &&
        data.shippingCompany.name !== 'N/A' &&
        data.shippingCompany.name.trim() !== ''
        ? data.shippingCompany.name
        : `${t('order_details.data_not_provided')}`;

    return {
      _id: data._id,
      orderNumber,
      orderType,
      orderStatus,
      application,
      hsCode,
      startingTime,
      validFrom,
      validTo,
      arrivalTime,
      origin,
      destination,
      shippingAgency,
      orderData: data,
    };
  });

  useEffect(() => {
    if (activeRowRef.current && tableContainerRef.current) {
      const rowElement = activeRowRef.current;
      const orderElement = tableContainerRef.current;

      const rowOffsetTop = rowElement.offsetTop;
      const rowHeight = rowElement.clientHeight;
      const newScrollTop =
        rowOffsetTop - orderElement.clientHeight / 2 + rowHeight / 2;
      const maxScrollTop =
        orderElement.scrollHeight - orderElement.clientHeight;
      const adjustedScrollTop = Math.max(
        0,
        Math.min(newScrollTop, maxScrollTop)
      );

      orderElement.scrollTo({
        top: adjustedScrollTop,
        behavior: 'smooth',
      });
    }
  }, [activeOrder]);

  const handleRowClick = (order: Order) => {
    if (onRowClick) onRowClick(order);
  };

  return (
    <div
      className="overflow-hidden rounded-lg border shadow-card"
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: 'var(--border-light)',
      }}
    >
      <div
        ref={tableContainerRef}
        className="orders-scroll max-h-[63vh] overflow-auto"
      >
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className="border-b px-4 py-3 text-left whitespace-nowrap"
                  style={{
                    minWidth: column.minWidth,
                    backgroundColor: 'var(--bg-surface-2)',
                    color: 'var(--text-main)',
                    borderColor: 'var(--border-light)',
                    fontWeight: 600,
                  }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: any) => (
                  <tr
                    key={row._id}
                    ref={row._id === activeOrder ? activeRowRef : null}
                    onClick={() => handleRowClick(row.orderData)}
                    className="cursor-pointer transition-colors"
                    style={{
                      backgroundColor:
                        row._id === activeOrder
                          ? 'var(--color-primary-light)'
                          : 'transparent',
                    }}
                  >
                    {columns.map((column) => {
                      const value = row[column.id as keyof typeof row];
                      let displayValue = value;

                      if (
                        ['pickupLocation', 'dropLocation'].includes(column.id) &&
                        value.length > 20
                      ) {
                        displayValue = value.substring(0, 20) + '...';
                      }

                      const showTitle =
                        ['pickupLocation', 'dropLocation'].includes(column.id) &&
                        value.length > 20;

                      const isDataNotProvided =
                        value === `${t('order_details.data_not_provided')}`;

                      if (typeof displayValue === 'string') {
                        displayValue = capitalizeWords(displayValue);
                      }

                      return (
                        <td
                          key={column.id}
                          className="border-b px-4 py-6 whitespace-nowrap text-[13px]"
                          style={{
                            borderColor: 'var(--border-light)',
                            color: isDataNotProvided
                              ? 'var(--text-muted)'
                              : 'var(--text-main)',
                            opacity: isDataNotProvided ? 0.7 : 1,
                          }}
                          {...(showTitle ? { title: value } : {})}
                        >
                          {displayValue}
                        </td>
                      );
                    })}
                  </tr>
                ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-6 text-center"
                  style={{ color: 'var(--text-main)' }}
                >
                  {t('orders_page.no_orders')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrdersTable;
